import { formatDate } from '@progress/kendo-angular-intl';
import { VehicleInfo } from '@spaq/utils/shared-constants';

import * as moment from 'moment-timezone';
import 'moment/locale/fr-ca';
import { Service } from './service-model';
import { SubscriptionModel, SubscriptionServiceModel } from './subscription-model';

export class Contract {
	get balance() {
		return this.subscription.rawData.balance.toFixed(2);
	}

	get cancelationDate() {
		if (!this.isCancelable) {
			return '---';
		}
		const cancelationNotice = Number(this.service.rawData.cancelationNotice || 0);

		let date = moment(this.now);
		const endDate = moment(this.service.rawData.availableUntil);

		if (date < this.startDate) {
			date = this.startDate;
		}

		if (cancelationNotice > 0) {
			if (this.service.rawData.cancelationNoticeTimeUnit === 'Day') {
				date.add(cancelationNotice, 'days');
			}

			if (this.service.rawData.cancelationNoticeTimeUnit === 'Month') {
				date.add(cancelationNotice, 'months');
			}
		}
		if (this.service.rawData.isCancelationFirstDayOfMonth) {
			date.add(1, 'months').startOf('month');
		}

		if (date > endDate) {
			return '---';
		}

		return date.format('LL');
	}

	get contractNo() {
		return this.rawData.contractNo || '---';
	}

	get cost() {
		return this.rawData.cost['invoiceTotalAmount'];
	}

	get createdAt() {
		return new Date(this._data.createdAt);
	}

	get effectiveDate() {
		if (this.subscriptionService.rawData.isApproved === false) {
			if (this.subscriptionService.rawData.effectiveDate) {
				if (moment().diff(moment(this.subscriptionService.rawData.effectiveDate || null), 'days') >= 0) {
					return "Jour de l'approbation";
				}
			}
		}
		if (
			this.service.period &&
			(this.service.period.force ||
				(this.service.serviceBillingType.toString() === 'LumpSum' && this.service.durationTimeUnit === 'Week'))
		) {
			return this.service.period.start;
		}
		return moment(this.subscriptionService.rawData.effectiveDate).tz('America/Montreal').format('LL');
	}

	get endDate() {
		return moment(this.service.rawData.availableUntil).tz('America/Montreal');
	}

	get endDateFormated() {
		if (
			this.service.period &&
			(this.service.period.force ||
				(this.service.serviceBillingType.toString() === 'LumpSum' && this.service.durationTimeUnit === 'Week'))
		) {
			return this.service.period.end;
		}
		if (this.subscriptionService.rawData.endDate) {
			if (moment(this.subscriptionService.rawData.endDate).get('year') === 9999) {
				return 'Indéterminée';
			}
			return moment(this.subscriptionService.rawData.endDate).tz('America/Montreal').format('LL');
		}
		return this.service.endDateFormatted;
	}

	get endDateRaw() {
		if (!this.service.rawData.isFixedDuration) {
			return 'Indéterminée';
		}
		let newDate = new Date(this.now);

		if (this.service.rawData.serviceStartRule.toString() === 'FirstDayOfNextMonth') {
			newDate = new Date(newDate.getFullYear(), newDate.getMonth() + 1, 1);
		}
		if (this.service.rawData.durationTimeUnit === 'Day') {
			newDate.setDate(newDate.getDate() + this.service.rawData.duration);
		}
		if (this.service.rawData.durationTimeUnit === 'Month') {
			newDate.setMonth(newDate.getMonth() + this.service.rawData.duration);
		}

		return formatDate(newDate, { date: 'long' }, 'fr-CA');
	}

	get exists() {
		return !!this.rawData.approve;
	}

	get isActive() {
		if (
			!this.subscription.rawData.isAccessOnHold &&
			!this.isFinished &&
			this.subscriptionService.rawData.isApproved &&
			!this.isWaiting
		) {
			return true;
		}
		return false;
	}

	get isCancelable() {
		if (!this._data.canceled && this.service.rawData.isCancelable && !this.isFinished) {
			return true;
		}
		return false;
	}

	get isDownloadable() {
		return !!this.rawData.pdfLink;
	}

	get isFinished() {
		if (this.now.getTime() > new Date(this.subscriptionService.rawData.endDate).getTime()) {
			return true;
		}
		return false;
	}

	get isMonthly() {
		return this.service.rawData.serviceBillingType.toString() === 'Monthly';
	}

	get isPaid() {
		return !!this.subscription.rawData.paymentMethod;
	}

	get isPayable() {
		if (this.sysPending || !this.subscriptionService.rawData.isApproved || this.isFinished) {
			return false;
		}
		return true;
	}

	get isPayableImmediately() {
		if (this.service.isApprovable) {
			return false;
		}
		if (Number(this.partialMonthCalculation) > 0) {
			return true;
		}
		if (this.service.rawData.serviceBillingType.toString() === 'LumpSum') {
			return true;
		}
	}

	get isPreauthorized() {
		if (this.service.isApprovable && this.service.rawData.serviceBillingType.toString() === 'LumpSum') {
			return true;
		}
		return false;
	}

	get isRecured() {
		if (this.service.rawData.serviceBillingType.toString() === 'Monthly') {
			return true;
		}
		if (
			this.service.rawData.serviceBillingType.toString() === 'Monthly' &&
			(this.service.rawData.serviceType.toString() === 'PublicWithApprobation' ||
				this.service.rawData.serviceType.toString() === 'PrivateWithApprobation')
		) {
			return true;
		}
		return false;
	}

	get isWaiting() {
		if (
			moment(this.now).tz('America/Montreal').startOf('day').toDate().getTime() <
			moment(this.subscriptionService.rawData.effectiveDate).tz('America/Montreal').startOf('day').toDate().getTime()
		) {
			return true;
		}
		return false;
	}

	get partialMonthCalculation() {
		if (true) {
			return this.service.partialPrice;
		}
	}

	get payment() {
		if (this.service.serviceBillingType === 'DAS') {
			return 'DAS';
		}
		if (this.subscription.rawData.paymentMethod.method === 'Cash' || this.subscription.rawData.paymentMethod.method === 'NotDefined') {
			return this.rawData.payment ? this.rawData.payment : null;
		}
		if (this.subscription.rawData.paymentMethod.method === 'DeductionAtSource') {
			return 'DAS';
		}
		return this.subscription.rawData.paymentMethod.method;
	}

	get paymentMethod() {
		if (this.subscription.rawData.paymentMethod.method === 'Cash' && this.subscription.rawData.paymentMethod.folioNo === null) {
			if (this.service.serviceBillingType.toString() === 'DAS') {
				return 'DAS';
			}
			return 'Internet';
		}
		switch (this.subscription.rawData.paymentMethod.method) {
			case 'NotDefined':
				return 'À Venir';
			case 'Cash':
				return 'Internet';
			case 'Credit':
			case 'CreditLumpSum':
				return 'Carte de crédit';
			case 'PreAuthorized':
				return 'Prélèvements préautorisés';
			case 'DeductionAtSource':
				return 'DAS';
			default:
				return 'DAS';
		}
	}

	get rawData() {
		return this._data;
	}

	get startDate() {
		let startDate = moment(this.createdAt).tz('America/Montreal').startOf('day');
		switch (this.service.rawData.serviceStartRule.toString()) {
			case 'FirstDayOfNextMonth':
				startDate.add(1, 'months').startOf('month');
				break;

			case 'FirstDayOfCurrentMonth':
				startDate.startOf('month');
				break;

			case 'FirstDayOfFlatRateDuration':
				startDate = moment(this.service.onSaleFrom).tz('America/Montreal').startOf('day');
				break;
			default:
				startDate = moment(this.createdAt).tz('America/Montreal').startOf('day');
				break;
		}

		return startDate;
	}

	get startDateFormated() {
		const endDate = moment(this.service.availableUntil).tz('America/Montreal');

		if (this.startDate > endDate) {
			return '---!!';
		}

		return moment(this.startDate).tz('America/Montreal').format('LL');
	}

	get status() {
		if (!this.isPaid) {
			if (this.isMonthly) {
				return 'En attente des informations bancaires';
			}
			return 'En attente de paiement';
		}

		if (this.subscriptionService.rawData.isApproved === false) {
			return "En attente d'approbation";
		}

		if (this.subscription.rawData.isAccessOnHold) {
			let status = 'En attente';

			if (this.subscription.rawData.accessOnHoldReason) {
				status = `${status} (${this.subscription.rawData.accessOnHoldReason})`;
			}
			return status;
		}

		if (this.isWaiting) {
			return 'Actif';
		}

		if (this.isFinished) {
			return 'Terminé';
		}

		return 'Actif';
	}

	get sysPending() {
		if (this.subscription.rawData.contractNo === 0 && this._data.approve) {
			return true;
		}
		return false;
	}

	get waitingPayment() {
		if (this._data.isPaid) {
			return false;
		}
		if (this._data.isRecured && this.service.serviceBillingType.toString() === 'Monthly') {
			return false;
		}
		return true;
	}

	approvedAt: Date;
	contract: any;
	now: Date;
	service: Service;
	subscription: SubscriptionModel;
	subscriptionService: SubscriptionServiceModel;
	vehicles: VehicleInfo[];
	private _data = {
		service: null,
		setServiceAsNull: '_data',
		park: null,
		subscription: null,
		subscriptionService: null,
		payment: null,
		vehicles: [],
		createdAt: null,
		canceled: false,
		approve: null,
		isPaid: null,
		cost: {},
		pdfLink: null,
		isRecured: false,
		paymentSent: false,
		contractNo: 0
	};

	constructor(contract) {
		this.contract = contract;
		this.service = new Service(contract.service);
		this.vehicles = contract.vehicles;

		this.subscription = new SubscriptionModel(contract.subscription.spaq);
		this.subscriptionService = new SubscriptionServiceModel(contract.subscriptionService);

		this.now = new Date();
		this.approvedAt = new Date();

		Object.keys(contract).forEach(k => {
			if (this._data[k] !== undefined) {
				this._data[k] = contract[k];
			}
		});
	}

	get hasMultipleServices() {
		if (this.contract.services && Object.values(this.contract.services).length > 1) {
			return true;
		}
		return false;
	}

	getService() {
		return this.contract.service;
	}

	getSubscription() {
		return this.contract.subscription;
	}

	set(name, value) {
		this._data[name] = value;
	}

	updateEndDate() {
		let newDate = new Date(this.now);

		if (this.service.rawData.serviceStartRule.toString() === 'FirstDayOfNextMonth') {
			newDate = new Date(newDate.getFullYear(), newDate.getMonth() + 1, 1);
		}
		if (this.service.rawData.serviceStartRule.toString() === 'FirstDayOfCurrentMonth') {
			newDate = new Date(newDate.getFullYear(), newDate.getMonth(), 1);
		}
		if (this.service.rawData.durationTimeUnit === 'Day') {
			newDate.setDate(newDate.getDate() + this.service.rawData.duration);
		}
		if (this.service.rawData.durationTimeUnit === 'Month') {
			newDate.setMonth(newDate.getMonth() + this.service.rawData.duration);
		}
		this.subscriptionService.set('endDate', 'FUCK');
	}

	updateStartDate() {
		let newDate = new Date(this.now);

		if (this.service.rawData.serviceStartRule.toString() === 'FirstDayOfNextMonth') {
			newDate = new Date(newDate.getFullYear(), newDate.getMonth() + 1, 1);
		}
		if (this.service.rawData.serviceStartRule.toString() === 'FirstDayOfCurrentMonth') {
			newDate = new Date(newDate.getFullYear(), newDate.getMonth(), 1);
		}

		this.subscriptionService.set('effectiveDate', newDate.toISOString());
	}
}
